import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { APIType } from 'src/util/use-api';
import {
  getConfirmedAtValue,
  getToggledReadIds,
} from 'src/util/notificationFormatting';
import { QUERIES } from 'src/util/globals';
import type { NotificationBatchUpdateRequest } from 'src/api-sdk';

type UseNotificationReadStateOptions = {
  api: APIType | null | undefined;
  // Report API errors in the caller when needed.
  onError?: (error: unknown) => void;
};

export const NOTIFICATION_READ_STATE_QUERY_KEY = [
  QUERIES.INBOX_NOTIFICATIONS,
  'read-state',
] as const;

export const isNotificationReadStateQueryKey = (queryKey: unknown) =>
  Array.isArray(queryKey) &&
  queryKey.length === NOTIFICATION_READ_STATE_QUERY_KEY.length &&
  queryKey.every(
    (value, index) => value === NOTIFICATION_READ_STATE_QUERY_KEY[index]
  );

export const isInboxNotificationQueryKey = (queryKey: unknown) =>
  Array.isArray(queryKey) &&
  queryKey[0] === QUERIES.INBOX_NOTIFICATIONS &&
  !isNotificationReadStateQueryKey(queryKey);

const getCachedReadIds = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.getQueryData<string[]>(NOTIFICATION_READ_STATE_QUERY_KEY) ?? [];

// Keeps notification read state in the shared React Query cache so avatar and
// inbox stay in sync through the same query key, without custom events or a
// separate provider.
export const useNotificationReadState = ({
  api,
  onError,
}: UseNotificationReadStateOptions) => {
  const queryClient = useQueryClient();
  const inFlightRef = useRef<Map<string, Promise<void>>>(new Map());
  const { data: readIds = [] } = useQuery({
    queryKey: NOTIFICATION_READ_STATE_QUERY_KEY,
    queryFn: () => Promise.resolve([] as string[]),
    initialData: [] as string[],
    staleTime: Infinity,
  });

  const refetchNotificationQueries = useCallback(() => {
    void queryClient.invalidateQueries({
      predicate: (query) => isInboxNotificationQueryKey(query.queryKey),
    });
  }, [queryClient]);

  const refetchUnreadCountQueries = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [QUERIES.INBOX_NOTIFICATIONS, 'unread-counts'],
    });
  }, [queryClient]);

  const updateReadIds = useCallback(
    (updater: (current: string[]) => string[]) => {
      queryClient.setQueryData<string[]>(
        NOTIFICATION_READ_STATE_QUERY_KEY,
        (current = []) => updater(current)
      );
    },
    [queryClient]
  );

  const readIdSet = useMemo(() => new Set(readIds), [readIds]);

  const isRead = useCallback((id: string) => readIdSet.has(id), [readIdSet]);

  const markUnreadLocally = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        return;
      }

      const unreadIdSet = new Set(ids);
      updateReadIds((current) => current.filter((id) => !unreadIdSet.has(id)));
    },
    [updateReadIds]
  );

  // Sync read ids returned by the server without erasing optimistic local reads.
  const syncConfirmedIds = useCallback(
    (
      itemIds: string[],
      confirmedIds: string[],
      options: { unreadIds?: string[] } = {}
    ) => {
      if (itemIds.length === 0) {
        return;
      }

      const confirmedIdSet = new Set(confirmedIds);
      const unreadIdSet = new Set(options.unreadIds ?? []);

      updateReadIds((current) => {
        const next = new Set(current);
        confirmedIdSet.forEach((id) => next.add(id));
        unreadIdSet.forEach((id) => next.delete(id));
        const nextIds = Array.from(next);

        if (
          nextIds.length === current.length &&
          nextIds.every((id) => current.includes(id))
        ) {
          return current;
        }

        return nextIds;
      });
    },
    [updateReadIds]
  );

  // Mark one notification as read.
  const toggleRead = useCallback(
    async (id: string) => {
      if (!api?.activitiesAPI) {
        return;
      }

      const inFlight = inFlightRef.current;
      if (inFlight.has(id)) {
        await inFlight.get(id);
      }

      const currentReadIds = getCachedReadIds(queryClient);
      const isCurrentlyRead = currentReadIds.includes(id);
      const newConfirmedAt = getConfirmedAtValue(isCurrentlyRead);
      updateReadIds((current) => getToggledReadIds(current, id));

      const request = (async () => {
        try {
          await api.activitiesAPI!.activitiesNotificationsPartialUpdate({
            notificationId: id,
            patchedNotificationRequest: { confirmed_at: newConfirmedAt },
          });
        } catch (error) {
          updateReadIds((current) => {
            const next = new Set(current);
            if (isCurrentlyRead) {
              next.add(id);
            } else {
              next.delete(id);
            }
            return Array.from(next);
          });
          onError
            ? onError(error)
            : console.error('Failed to update notification state', error);
        } finally {
          inFlight.delete(id);
          refetchNotificationQueries();
          refetchUnreadCountQueries();
        }
      })();

      inFlight.set(id, request);
      await request;
    },
    [
      api,
      onError,
      queryClient,
      refetchNotificationQueries,
      refetchUnreadCountQueries,
      updateReadIds,
    ]
  );

  // Mark a group of notifications as read.
  const setReadStateForIds = useCallback(
    async (ids: string[], shouldRead: boolean) => {
      if (ids.length === 0) {
        return;
      }

      if (!api?.activitiesAPI) {
        return;
      }

      const previousReadIdSet = new Set(getCachedReadIds(queryClient));
      const previousReadStateById = new Map(
        ids.map((id) => [id, previousReadIdSet.has(id)])
      );
      const newConfirmedAt = shouldRead ? new Date().toISOString() : null;

      updateReadIds((current) => {
        const next = new Set(current);
        ids.forEach((id) => {
          if (shouldRead) {
            next.add(id);
          } else {
            next.delete(id);
          }
        });
        return Array.from(next);
      });

      const updates = ids.map((id) => ({
        id,
        request: api.activitiesAPI.activitiesNotificationsPartialUpdate({
          notificationId: id,
          patchedNotificationRequest: { confirmed_at: newConfirmedAt },
        }),
      }));
      const results = await Promise.allSettled(
        updates.map((update) => update.request)
      );
      const failedIds = results
        .map((result, index) =>
          result.status === 'rejected' ? updates[index]?.id : undefined
        )
        .filter((id): id is string => Boolean(id));

      if (failedIds.length > 0) {
        const firstError = results.find(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected'
        )?.reason;

        updateReadIds((current) => {
          const next = new Set(current);
          failedIds.forEach((id) => {
            if (previousReadStateById.get(id)) {
              next.add(id);
            } else {
              next.delete(id);
            }
          });
          return Array.from(next);
        });
        onError
          ? onError(firstError)
          : console.error('Failed to update notification state', firstError);
      }

      refetchNotificationQueries();
      refetchUnreadCountQueries();
    },
    [
      api,
      onError,
      queryClient,
      refetchNotificationQueries,
      refetchUnreadCountQueries,
      updateReadIds,
    ]
  );

  // Mark all given ids as read via the batch API endpoint.
  const markAllAsRead = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0 || !api?.activitiesAPI) {
        return;
      }

      const previousReadIdSet = new Set(getCachedReadIds(queryClient));
      const previousReadStateById = new Map(
        ids.map((id) => [id, previousReadIdSet.has(id)])
      );

      updateReadIds((current) => {
        const next = new Set(current);
        ids.forEach((id) => next.add(id));
        return Array.from(next);
      });

      try {
        const payload: NotificationBatchUpdateRequest[] = ids.map((id) => ({
          external_id: id,
        }));
        await api.activitiesAPI.activitiesNotificationsBatchUpdatePartialUpdate(
          {
            notificationBatchUpdateRequest: payload,
          }
        );
      } catch (error) {
        updateReadIds((current) => {
          const next = new Set(current);
          ids.forEach((id) => {
            if (previousReadStateById.get(id)) {
              next.add(id);
            } else {
              next.delete(id);
            }
          });
          return Array.from(next);
        });
        onError
          ? onError(error)
          : console.error('Failed to update notification state', error);
      } finally {
        refetchNotificationQueries();
        refetchUnreadCountQueries();
      }
    },
    [
      api,
      onError,
      queryClient,
      refetchNotificationQueries,
      refetchUnreadCountQueries,
      updateReadIds,
    ]
  );

  return {
    readIds,
    isRead,
    markUnreadLocally,
    toggleRead,
    setReadStateForIds,
    markAllAsRead,
    syncConfirmedIds,
  };
};
